import {
  Box,
  Flex,
  PrimaryButton,
  Stack,
  TertiaryButton,
  Text,
} from "flicket-ui";
import { useRouter } from "next/router";
import { getImageSrc, withPropagation } from "~lib";
import { EventKind, ExtendedFile } from "~graphql/sdk";
import { useOrganization } from "~hooks";
import useCaptureClick from "~telemetry/traces/useCaptureClick";
import {
  AttributeNames,
  InteractionNames,
} from "~telemetry/enums/AttributeNames";
import { FormatDateOptions } from "@flicket/utils";
import { formatEventDateRange } from "~lib/helpers/formatDate";
import { DateDisplay, formatDateForDisplay } from "./events.DateDisplay";
import { FixedRatioImage } from "~components/common";
import {
  getEventVenueLocation,
  VenueAddressLike,
} from "~lib/helpers/getEventVenueLocation";

const medium = "sm";

export interface EventCardProps {
  type: "event" | "membership";
  title: string;
  id: string;
  venue: VenueAddressLike;
  timeZone: string;
  locale: string;
  startDate: string;
  endDate: string;
  eventKind: string;
  hasPublicRelease: boolean;
  logo: ExtendedFile | null;
  isWaitlistAvailable: boolean;
  startDateForLinkedPresale: string | undefined;
}

export const EventCard = ({
  id,
  title,
  hasPublicRelease,
  startDate,
  endDate,
  eventKind,
  logo,
  type,
  venue,
  timeZone,
  locale,
  isWaitlistAvailable,
  startDateForLinkedPresale,
}: EventCardProps) => {
  const router = useRouter();
  const isDigitalEvent = eventKind === EventKind.Digital;
  const { organization, hasFeature } = useOrganization();

  const navigateEvent = () => {
    void router.push(type === "event" ? `/events/${id}` : `/memberships/${id}`);
  };
  const navigateReservation = () => {
    void router.push(
      type === "event"
        ? `/events/${id}/reservation`
        : `/memberships/${id}/reservation`
    );
  };
  const navigateRegistration = () => {
    void router.push(`/events/${id}?modal=registration`);
  };

  const captureClick = useCaptureClick({
    forwardToNest: true,
    eventId: id || "",
    pageName: "event-list-page",
  });

  const dateOptions: FormatDateOptions = {
    showStartTime: !hasFeature("hideEventTime"),
    timeZone,
    locale,
  };

  const location = getEventVenueLocation(venue);

  const imageSrc = getImageSrc(logo, "");

  const { day, month } = formatDateForDisplay(startDate, dateOptions);

  const dateString = formatEventDateRange(startDate, endDate, {
    format: "basicShort",
    ...dateOptions,
  });

  // Check if certain organizations should show the buttons (like in the original code)
  const shouldShowButtons =
    organization.id !== "58106ce0-e1cd-47cb-9c59-0d0192ca6df8" &&
    organization.id !== "45339f71-c0bb-41a9-b642-7d30f1321a84";

  const isRegistrationAvailable = (() => {
    if (!isWaitlistAvailable) {
      return false;
    }

    // no presale linked to registration
    if (!startDateForLinkedPresale) return true;

    // has a presale linked but not started yet
    return new Date(startDateForLinkedPresale) > new Date();
  })();

  const showPrimaryButton = hasPublicRelease || isRegistrationAvailable;

  const handlePrimaryButtonNavigation = hasPublicRelease
    ? navigateReservation
    : navigateRegistration;

  const primaryButtonLabel = (() => {
    if (hasPublicRelease) {
      return isDigitalEvent ? "Get access" : "Buy tickets";
    }

    return "Register";
  })();

  return (
    <Stack
      gap={3}
      py={2}
      flex="1"
      direction={{
        _: "vertical",
        [medium]: "horizontal",
      }}
      minHeight="180px"
      onClick={(e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        navigateEvent && withPropagation(e, () => void navigateEvent());
      }}
      borderTop="1px solid"
      borderTopColor="N200"
      cursor="pointer"
    >
      {!!imageSrc && (
        <Box>
          <FixedRatioImage
            alt={title}
            aspectRatio="5 / 2"
            src={imageSrc}
            metaData={logo?.metaData}
            width={{
              _: "100%",
              [medium]: "370px",
              ["md"]: "370px",
            }}
            overflow="hidden"
            borderRadius={"md"}
            useNearFitCover={true}
          />
        </Box>
      )}

      <Stack
        direction={{
          _: "vertical",
          md: "horizontal",
        }}
        alignItems={{ md: "center" }}
        flex="1"
        flexWrap={"nowrap"}
        gap={2}
      >
        <Stack
          justifyContent={{
            _: "space-between",
            [medium]: "flex-start",
          }}
          direction={{
            _: "vertical",
          }}
          alignItems={{
            _: "stretch",
            [medium]: "flex-start",
          }}
          flex={1}
          gap={2}
        >
          <Stack
            direction={"horizontal"}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
          >
            <Stack direction={"vertical"} alignItems={"flex-start"}>
              <Text as="h1" variant="header.S" fontWeight="bold" mb={1}>
                {title}
                {isDigitalEvent ? " (Online Event)" : ""}
              </Text>
              <Text variant="regular">{dateString}</Text>
              <Text variant="regular">{location}</Text>
            </Stack>
            <Flex
              flexShrink={0}
              justifyContent={"flex-end"}
              textAlign={"center"}
              display={{ [medium]: "none" }}
              pl={5}
            >
              <DateDisplay day={day} month={month} />
            </Flex>
          </Stack>
        </Stack>
        {shouldShowButtons && (
          <Stack
            justifyContent={{ _: "flex-start", md: "flex-end" }}
            alignItems="flex-start"
            direction="horizontal"
            gap={2}
            flex={1}
          >
            <TertiaryButton
              alignSelf={{ _: "center", [medium]: "flex-start" }}
              flex={{
                _: "1",
                [medium]: "unset",
              }}
              onClick={(e) => {
                navigateEvent && withPropagation(e, () => void navigateEvent());
              }}
            >
              Event info
            </TertiaryButton>

            {showPrimaryButton && (
              <PrimaryButton
                alignSelf={{
                  _: "stretch",
                  [medium]: "flex-start",
                }}
                flex={{
                  _: "1",
                  [medium]: "unset",
                }}
                onClick={captureClick(
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  (e) => withPropagation(e, handlePrimaryButtonNavigation),
                  InteractionNames.BUTTON_PRIMARY,
                  {
                    [AttributeNames.BUTTON_LABEL]: primaryButtonLabel,
                  }
                )}
              >
                {primaryButtonLabel}
              </PrimaryButton>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
